import * as hashes from "common/hashes";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as staticDataService from "services/staticDataService";
import * as analyticsStore from "store/analytics";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as submissionSelector from "store/selectors/submission/submissionSelector";
import * as userSelectors from "store/selectors/user/userSelectors";

const useSubmissionHash = (submission) => {
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    (async () => {
      const excluded_schema_modules = ["analytics"];

      const relevantSubmission = Object.fromEntries(
        Object.entries(submission ?? {}).filter(
          ([key]) => !excluded_schema_modules.includes(key)
        )
      );
      const hash = await hashes.hashDict(relevantSubmission);
      setCurrent(hash);
    })();
  }, [submission, setCurrent]);

  return current;
};

export const usePricing = ({ pricingMethod = "programPricing" } = {}) => {
  pricingMethod = pricingMethod ?? "programPricing";

  const dispatch = useDispatch();

  const submission = useDebouncedValue(
    useSelector(submissionSelector.selectResolvedSubmissionWithoutInput)
  );

  const isUserReadOnly = useSelector(userSelectors.isUserReadOnly);

  const analytics = useSelector(analyticsStore.select);
  const pricing = analytics?.[pricingMethod];

  const submissionHash = useSubmissionHash(submission);
  const latestSubmissionHash = pricing?.submissionHash;

  const { data: analyticsConfig } = staticDataService.useConfigQuery(
    "analytics"
  );
  const pricingConfig = analyticsConfig?.[pricingMethod];

  const engine = analytics?.[pricingMethod]?.engine ?? pricingConfig?.engine;
  const metric = pricingConfig?.metric ?? "program_pricing";

  const [triggerAnalytics] = analyticsService.useLazySubmissionAnalyticsQuery();

  const [isFetching, setIsFetching] = useState(false);

  const fetchPricing = useCallback(
    async (submission, submissionHash) => {
      if (isUserReadOnly || !engine || !metric) {
        return;
      }

      setIsFetching(true);
      try {
        const result = await triggerAnalytics({
          engine,
          metric,
          submission,
        });
        if (result.data) {
          dispatch(
            analyticsStore.update({
              [pricingMethod]: {
                ...result.data,
                engine,
                submissionHash,
              },
            })
          );
        }
      } finally {
        setIsFetching(false);
      }
    },
    [isUserReadOnly, engine, metric, pricingMethod, triggerAnalytics, dispatch]
  );

  useEffect(() => {
    if (
      submissionHash &&
      (!latestSubmissionHash || latestSubmissionHash !== submissionHash)
    ) {
      fetchPricing(submission, submissionHash);
    }
  }, [
    fetchPricing,
    analyticsConfig,
    latestSubmissionHash,
    submissionHash,
    submission,
  ]);

  return {
    data: pricing,
    isFetching,
    refetch: () => fetchPricing(submission, submissionHash),
  };
};
